import CogIcon from "@components/icons/CogIcon";
import { Tooltip } from "flowbite-react";
import { useState } from "react";
import DeleteIcon from "../icons/DeleteIcon";
import EditIcon from "../icons/EditIcon";
import EyeIcon from "../icons/EyeIcon";
import ModalDelete from "../Modals/ModalDelete";

const ListTable = ({
	onDelete,
	list = [],
	onEdit,
	isAction = true,
	columns = [],
	handleRowClick,
	openModalConfig,
	handleConfig,
	message = "",
	viewColumn = false,
	editColumn = false,
	deleteColumn = false,
	configColumn = false,
	showConfig = false,
	name = "",
	nickname = "",
	siteName = "",
	role = "",
	fetchSortedData,
	sorted_column = "",
	sorted_order = "",
	hasRelations = false,
	alertBeforeDelete = "",
	hasUsersRelations = false,
	alertUsersBeforeDelete = "",
	isAdmin = false,
	handleViewDetails,
	viewColumnData = false,

	disableDeleteCondition = () => false,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [selectedName, setSelectedName] = useState(null);
	const [selectedEmail, setSelectedEmail] = useState(null);
	const [selectedSiteName, setSelectedSiteName] = useState(null);
	const [selectedAccountName, setSelectedAccountName] = useState(null);

	const openModal = (id, name, email, siteName, accName) => {
		setSelectedId(id);
		setSelectedName(name);
		setSelectedEmail(email);
		setSelectedSiteName(siteName);
		setSelectedAccountName(accName);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedId(null);
		setSelectedName(null);
		setSelectedSiteName(null);
		setSelectedAccountName(null);
	};

	const getValue = (row, field) => {
		const value = field?.split(".").reduce((acc, part) => {
			if (!acc) return null;

			const match = part.match(/^(\w+)(\[(\d+)\])?$/);

			if (match) {
				const [, key, , index] = match;
				acc = acc[key];
				if (index !== undefined) acc = acc[Number(index)];
			} else {
				acc = acc[part];
			}

			return acc;
		}, row);
		if (Array.isArray(value)) {
			return value.length > 0
				? value
						.map((item) => {
							if (typeof item === "object" && item !== null) {
								if (item.threshold !== undefined) return `${item.threshold}%`;
								if (item.display_name) return item.display_name;
								if (item.name) return item.name;
								if (item.id !== undefined) return item.id;
							}
							return item;
						})
						.join(", ")
				: "No Data";
		}

		return value !== undefined && value !== null ? value : "No Data";
	};

	const handleSort = (column) => {
		const order = sorted_column === column && sorted_order === "asc" ? "desc" : "asc";
		fetchSortedData(column, order);
	};

	const getRelationWarning = (row) => {
		if (row.has_kits_stock) {
			return {
				hasRelation: true,
				message:
					"This distributor has kits in stock and cannot be deleted. Please remove or reassign the kits first.",
			};
		}

		return {
			hasRelation: false,
			message: "",
		};
	};

	return (
		<>
			<div className="custom-scrollbar relative grid overflow-x-auto bg-navbar-bg shadow-md md:rounded-lg">
				<table className="min-w-full text-left text-[11px] text-gray-400 sm:text-sm rtl:text-right">
					<thead className="bg-dark-blue bg-opacity-40 text-gray-400 text-xs uppercase">
						<tr>
							{columns.map((col, index) => {
								const isSorted = sorted_column === col.field;
								const sortIcon = isSorted
									? sorted_order === "asc"
										? "M12 4l-6 8h12z"
										: "M12 20l6-8H6z"
									: "M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086l-3.427-5.05a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z";

								return (
									<th
										key={index}
										scope="col"
										className={`w-[200px] cursor-pointer border-3 px-2 py-4 text-start text-[10px] text-gray-400 sm:text-xs`}
										onClick={() => {
											if (col.sortable) {
												handleSort(col.field);
											}
										}}
									>
										<div className="flex items-center text-[10px] sm:text-xs">
											{col.header}
											{col.sortable && (
												<svg
													className={`ml-1 h-3 w-3 transition-all ${
														isSorted ? "text-blue-gray text-opacity-80" : "text-blue-gray"
													}`}
													fill="currentColor"
													viewBox="0 0 24 24"
												>
													<path d={sortIcon} />
												</svg>
											)}
										</div>
									</th>
								);
							})}
							{isAction && (
								<th
									scope="col"
									className="sticky right-0 w-[150px] border-3 border-b-[#7b7b7d] bg-nav-action p-3 text-center text-[10px] text-gray-400 shadow-[inset_6px_0_6px_-6px_rgba(0,0,0,0.6)] sm:text-xs"
								>
									Action
								</th>
							)}
						</tr>
					</thead>
					<tbody>
						{Array.isArray(list) && list.length > 0 ? (
							list.map((row, index) => (
								<tr
									key={index}
									className="border-gray-700 border-b bg-navbar-bg hover:bg-blue-gray hover:bg-opacity-40"
								>
									<th
										scope="row"
										className={`w-[200px] max-w-xs cursor-pointer items-center justify-center p-3 text-start font-medium text-[10px] text-white sm:text-xs`}
										onClick={(event) => {
											if (handleRowClick) handleRowClick(row.id, row.name, event);
										}}
									>
										<div
											className="whitespace-normal break-words"
											title={getValue(row, columns[0]?.field)}
										>
											{columns[0]?.content
												? columns[0]?.content(row)
												: getValue(row, columns[0]?.field)}
										</div>
									</th>
									{columns.slice(1).map((col, colIndex) => (
										<td
											className={`w-[200px] max-w-xs items-center justify-center p-2 text-start text-[10px] sm:text-xs`}
											key={colIndex}
										>
											{col?.content ? col?.content(row) : getValue(row, col.field)}
										</td>
									))}
									{isAction && (
										<td className="sticky right-0 z-10 min-w-[150px] items-center justify-center border-3 border-b-[#7b7b7d] bg-navbar-bg p-2 text-center shadow-[inset_5px_0_5px_-5px_rgba(0,0,0,0.5)]">
											{" "}
											<div className="inline-flex">
												{configColumn && (
													<button
														type="button"
														className="me-2 rounded-xl bg-button-bg px-2 py-1 font-medium text-[10px] text-white hover:bg-button-hover focus:outline-none sm:px-2 sm:py-2 sm:text-sm"
														onClick={() => openModalConfig(row.id)}
													>
														<Tooltip
															animation="duration-150"
															content="Configure"
															className="bg-dark-blue bg-opacity-80"
														>
															<CogIcon className={"h-5 w-5 text-white"} />
														</Tooltip>
													</button>
												)}
												{viewColumn && (
													<button
														type="button"
														className="me-2 rounded-xl bg-button-bg px-2 py-1 font-medium text-[10px] text-white hover:bg-button-hover focus:outline-none sm:px-2 sm:py-2 sm:text-sm"
														onClick={(event) =>
															// handleRowClick(row.id, row.name, event)
															handleViewDetails(row.id, row.name, event)
														}
													>
														<Tooltip
															animation="duration-150"
															content="View details"
															className="bg-dark-blue bg-opacity-80"
														>
															<EyeIcon className={"h-5 w-5 text-white"} />
														</Tooltip>
													</button>
												)}
												{viewColumnData && (
													<button
														type="button"
														className="me-2 rounded-xl bg-button-bg px-2 py-1 font-medium text-[10px] text-white hover:bg-button-hover focus:outline-none sm:px-2 sm:py-2 sm:text-sm"
														onClick={(event) => handleViewDetails(row, event)}
													>
														<Tooltip
															animation="duration-150"
															content="View details"
															className="bg-dark-blue bg-opacity-80"
														>
															<EyeIcon className={"h-5 w-5 text-white"} />
														</Tooltip>
													</button>
												)}
												{showConfig && (
													<button
														type="button"
														className="me-2 rounded-xl bg-button-bg px-2 py-1 font-medium text-[10px] text-white hover:bg-button-hover focus:outline-none sm:px-2 sm:py-2 sm:text-sm"
														onClick={() => handleConfig(row)}
													>
														<Tooltip
															animation="duration-150"
															content="Edit"
															className="bg-dark-blue bg-opacity-80"
														>
															<CogIcon className={"h-5 w-5 text-white"} />
														</Tooltip>
													</button>
												)}
												{editColumn && (
													<button
														type="button"
														className="me-2 rounded-xl bg-button-bg px-2 py-1 font-medium text-[10px] text-white hover:bg-button-hover focus:outline-none sm:px-2 sm:py-2 sm:text-sm"
														onClick={() => onEdit(row.id)}
													>
														<Tooltip
															animation="duration-150"
															content="Edit"
															className="bg-dark-blue bg-opacity-80"
														>
															<EditIcon className={"h-5 w-5 text-white"} />
														</Tooltip>
													</button>
												)}
												{deleteColumn && (
													<button
														disabled={disableDeleteCondition(row)}
														type="button"
														className={`me-2 rounded-xl px-2 py-1 font-medium text-[10px] text-white focus:outline-none sm:px-2 sm:py-2 sm:text-sm ${
															disableDeleteCondition(row)
																? "button-hover cursor-not-allowed bg-button-bg bg-opacity-30 opacity-50"
																: "bg-button-bg hover:bg-button-hover"
														}`}
														onClick={() =>
															openModal(row.id, row.name, row.email, row.site_name, row.nickname)
														}
													>
														<Tooltip
															animation="duration-150"
															content="Delete"
															className="bg-dark-blue bg-opacity-80"
														>
															<DeleteIcon className={"h-5 w-5 text-white"} />
														</Tooltip>
													</button>
												)}
											</div>
										</td>
									)}
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={columns.length + 1}
									className="h-64 px-4 py-4 text-center text-gray-500 text-xs"
								>
									No data available
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			<ModalDelete
				isOpen={isModalOpen}
				closeModal={closeModal}
				unitName={selectedName}
				name={name}
				nickname={nickname}
				siteName={selectedSiteName}
				email={selectedEmail}
				role={role}
				accName={selectedAccountName}
				handleDelete={onDelete}
				id={selectedId}
				response_message={message}
				hasRelations={
					typeof hasRelations === "function"
						? hasRelations({ ...list.find((r) => r.id === selectedId) })
						: hasRelations
				}
				hasUsersRelations={
					typeof hasUsersRelations === "function"
						? hasUsersRelations({ ...list.find((r) => r.id === selectedId) })
						: hasUsersRelations
				}
				alertBeforeDelete={alertBeforeDelete}
				alertUsersBeforeDelete={alertUsersBeforeDelete}
			/>
		</>
	);
};

export default ListTable;
